import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import DashboardLayout from '../components/DashboardLayout';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = {
  meta: { name: 'Meta Ads', color: '#1877F2', bg: '#e7f0fd' },
  google: { name: 'Google Ads', color: '#EA4335', bg: '#fdecea' },
  tiktok: { name: 'TikTok Ads', color: '#000000', bg: '#f0f0f0' },
};

const DATE_RANGES = [
  { value: '7', label: 'Últimos 7 días' },
  { value: '30', label: 'Últimos 30 días' },
  { value: '60', label: 'Últimos 60 días' },
  { value: '90', label: 'Últimos 90 días' },
  { value: 'all', label: 'Todo el tiempo' },
];

const SECTIONS = [
  { id: 'summary', label: 'Resumen General', icon: '📊', default: true },
  { id: 'platforms', label: 'Desglose por Plataforma', icon: '🔀', default: true },
  { id: 'campaigns', label: 'Detalle de Campañas', icon: '📋', default: true },
  { id: 'top_campaigns', label: 'Top Campañas por Gasto', icon: '🏆', default: false },
  { id: 'performance', label: 'Métricas de Rendimiento', icon: '📈', default: false },
];

const ALL_METRICS = [
  { key: 'spent', label: 'Gasto', format: 'currency', default: true },
  { key: 'impressions', label: 'Impresiones', format: 'number', default: true },
  { key: 'clicks', label: 'Clicks', format: 'number', default: true },
  { key: 'conversions', label: 'Conversiones', format: 'number', default: true },
  { key: 'ctr', label: 'CTR', format: 'percent', default: true },
  { key: 'cpc', label: 'CPC', format: 'currency', default: true },
  { key: 'roi', label: 'ROI', format: 'percent', default: true },
  { key: 'budget', label: 'Presupuesto', format: 'currency', default: false },
  { key: 'cpm', label: 'CPM', format: 'currency', default: false },
  { key: 'cpa', label: 'CPA', format: 'currency', default: false },
  { key: 'roas', label: 'ROAS', format: 'number', default: false },
];

const FREQ_OPTIONS = [
  { value: 'daily', label: 'Diario' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
];

export default function Reports() {
  const { token, user } = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('design');
  const [reportTitle, setReportTitle] = useState('Informe de Marketing Digital');
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [sections, setSections] = useState(() => {
    const s = {};
    SECTIONS.forEach(sec => s[sec.id] = sec.default);
    return s;
  });
  const [metrics, setMetrics] = useState(() => {
    const m = {};
    ALL_METRICS.forEach(met => m[met.key] = met.default);
    return m;
  });
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleFreq, setScheduleFreq] = useState('monthly');
  const [scheduleEmail, setScheduleEmail] = useState('');
  const [scheduleDay, setScheduleDay] = useState('1');
  const [scheduleSaved, setScheduleSaved] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [c, cl] = await Promise.all([
          axios.get(`${API}/campaigns${clientId ? `?client_id=${clientId}` : ''}`, { headers }),
          axios.get(`${API}/clients`, { headers }),
        ]);
        setCampaigns(c.data);
        setClients(cl.data);
        if (user?.email) setScheduleEmail(user.email);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getFilteredCampaigns = () => {
    let filtered = [...campaigns];
    if (selectedClient !== 'all') filtered = filtered.filter(c => c.client_id === selectedClient);
    if (selectedPlatform !== 'all') filtered = filtered.filter(c => c.platform === selectedPlatform);
    if (dateRange !== 'all') {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - parseInt(dateRange));
      filtered = filtered.filter(c => !c.date || new Date(c.date) >= cutoff);
    }
    return filtered;
  };

  const fmt = (val, type) => {
    if (val === undefined || val === null) return '-';
    if (type === 'currency') return '$' + Number(val).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (type === 'percent') return Number(val).toFixed(2) + '%';
    if (type === 'number') {
      if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
      if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
      return Number(val).toLocaleString();
    }
    return val;
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const filtered = getFilteredCampaigns();
      if (filtered.length === 0) {
        alert('No hay datos para generar el informe');
        setGenerating(false);
        return;
      }
      const doc = new jsPDF();
      const W = doc.internal.pageSize.getWidth();
      let y = 0;
      doc.setFillColor(37, 99, 235);
      doc.rect(0, 0, W, 45, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(reportTitle, 15, 22);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const dateLabel = DATE_RANGES.find(d => d.value === dateRange)?.label || 'Todo el tiempo';
      doc.text('Periodo: ' + dateLabel, 15, 32);
      doc.text('Generado: ' + new Date().toLocaleDateString('es-CL'), W - 15, 32, { align: 'right' });
      if (selectedClient !== 'all') {
        const cl = clients.find(c => c.id === selectedClient);
        if (cl) doc.text('Cliente: ' + cl.name, W - 15, 22, { align: 'right' });
      }
      doc.setTextColor(0, 0, 0);
      y = 55;
      if (sections.summary) {
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Resumen General', 15, y); y += 8;
        const ts = filtered.reduce((s, c) => s + (c.spent || 0), 0);
        const ti = filtered.reduce((s, c) => s + (c.impressions || 0), 0);
        const tc = filtered.reduce((s, c) => s + (c.clicks || 0), 0);
        const tv = filtered.reduce((s, c) => s + (c.conversions || 0), 0);
        const ac = filtered.length ? filtered.reduce((s, c) => s + (c.ctr || 0), 0) / filtered.length : 0;
        const ap = tc ? ts / tc : 0;
        const ar = filtered.length ? filtered.reduce((s, c) => s + (c.roi || 0), 0) / filtered.length : 0;
        const am = ti ? (ts / ti) * 1000 : 0;
        const aa = tv ? ts / tv : 0;
        const rows = [['Total Campanas', String(filtered.length)]];
        if (metrics.spent) rows.push(['Gasto Total', fmt(ts, 'currency')]);
        if (metrics.impressions) rows.push(['Impresiones', fmt(ti, 'number')]);
        if (metrics.clicks) rows.push(['Clicks', fmt(tc, 'number')]);
        if (metrics.conversions) rows.push(['Conversiones', fmt(tv, 'number')]);
        if (metrics.ctr) rows.push(['CTR Promedio', fmt(ac, 'percent')]);
        if (metrics.cpc) rows.push(['CPC Promedio', fmt(ap, 'currency')]);
        if (metrics.cpm) rows.push(['CPM Promedio', fmt(am, 'currency')]);
        if (metrics.cpa) rows.push(['CPA Promedio', fmt(aa, 'currency')]);
        if (metrics.roi) rows.push(['ROI Promedio', fmt(ar, 'percent')]);
        if (metrics.budget) rows.push(['Presupuesto Total', fmt(filtered.reduce((s, c) => s + (c.budget || 0), 0), 'currency')]);
        autoTable(doc, { startY: y, head: [['Metrica', 'Valor']], body: rows, theme: 'striped', headStyles: { fillColor: [37, 99, 235] }, columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' } }, styles: { fontSize: 10 } });
        y = doc.lastAutoTable.finalY + 12;
      }
      if (sections.platforms) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Desglose por Plataforma', 15, y); y += 8;
        const pks = [...new Set(filtered.map(c => c.platform))];
        const pr = pks.map(p => {
          const pc = filtered.filter(c => c.platform === p);
          return [PLATFORMS[p]?.name || p, pc.length, fmt(pc.reduce((s, c) => s + (c.spent || 0), 0), 'currency'), fmt(pc.reduce((s, c) => s + (c.clicks || 0), 0), 'number'), fmt(pc.reduce((s, c) => s + (c.conversions || 0), 0), 'number'), fmt(pc.reduce((s, c) => s + (c.roi || 0), 0) / pc.length, 'percent')];
        });
        autoTable(doc, { startY: y, head: [['Plataforma', 'Campanas', 'Gasto', 'Clicks', 'Conv.', 'ROI']], body: pr, theme: 'striped', headStyles: { fillColor: [16, 185, 129] }, styles: { fontSize: 9 } });
        y = doc.lastAutoTable.finalY + 12;
      }
      if (sections.campaigns) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Detalle de Campanas', 15, y); y += 8;
        const sm = ALL_METRICS.filter(m => metrics[m.key]);
        autoTable(doc, { startY: y, head: [['Campana', 'Plataforma', ...sm.map(m => m.label)]], body: filtered.map(c => [c.name.length > 22 ? c.name.substring(0, 22) + '...' : c.name, PLATFORMS[c.platform]?.name || c.platform, ...sm.map(m => fmt(c[m.key], m.format))]), theme: 'striped', headStyles: { fillColor: [124, 58, 237] }, styles: { fontSize: 8 } });
        y = doc.lastAutoTable.finalY + 12;
      }
      if (sections.top_campaigns) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Top 10 Campanas por Gasto', 15, y); y += 8;
        const top = [...filtered].sort((a, b) => (b.spent || 0) - (a.spent || 0)).slice(0, 10);
        autoTable(doc, { startY: y, head: [['#', 'Campana', 'Plataforma', 'Gasto', 'Clicks', 'Conv.']], body: top.map((c, i) => [i + 1, c.name.length > 25 ? c.name.substring(0, 25) + '...' : c.name, PLATFORMS[c.platform]?.name || c.platform, fmt(c.spent, 'currency'), fmt(c.clicks, 'number'), fmt(c.conversions, 'number')]), theme: 'striped', headStyles: { fillColor: [245, 158, 11] }, styles: { fontSize: 9 } });
        y = doc.lastAutoTable.finalY + 12;
      }
      if (sections.performance) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        doc.text('Metricas de Rendimiento', 15, y); y += 8;
        const active = filtered.filter(c => c.status === 'active' || c.status === 'enabled');
        autoTable(doc, { startY: y, head: [['Indicador', 'Valor']], body: [['Campanas Activas', String(active.length)], ['Campanas Pausadas', String(filtered.filter(c => c.status === 'paused').length)], ['Gasto en Activas', fmt(active.reduce((s, c) => s + (c.spent || 0), 0), 'currency')], ['Mejor CTR', fmt(Math.max(...filtered.map(c => c.ctr || 0)), 'percent')], ['Mejor ROI', fmt(Math.max(...filtered.map(c => c.roi || 0)), 'percent')]], theme: 'striped', headStyles: { fillColor: [239, 68, 68] }, columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' } }, styles: { fontSize: 10 } });
      }
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(150, 150, 150);
        doc.text('Generado por AdPulse AI - adpulseia.com', 15, 290);
        doc.text('Pagina ' + i + ' de ' + pageCount, W - 15, 290, { align: 'right' });
      }
      doc.save(reportTitle.replace(/\s+/g, '_') + '_' + new Date().toISOString().slice(0, 10) + '.pdf');
    } catch (e) {
      console.error('Error generating PDF:', e);
      alert('Error generando el PDF');
    } finally {
      setGenerating(false);
    }
  };

  const saveSchedule = () => { setScheduleSaved(true); setTimeout(() => setScheduleSaved(false), 3000); };
  const filtered = getFilteredCampaigns();
  const totalSpent = filtered.reduce((s, c) => s + (c.spent || 0), 0);
  const totalClicks = filtered.reduce((s, c) => s + (c.clicks || 0), 0);
  const totalConv = filtered.reduce((s, c) => s + (c.conversions || 0), 0);
  const card = { background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' };
  const label = { fontSize: '12px', color: '#64748b', fontWeight: '500', marginBottom: '6px' };
  const sel = { width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', background: 'white', cursor: 'pointer', outline: 'none' };

  return (
    <DashboardLayout>
      <div style={{ padding: '32px', maxWidth: '1100px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: '700', margin: 0, color: '#0f172a' }}>Informes PDF</h1>
            <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>Diseña y programa reportes profesionales para tus clientes</p>
          </div>
          <button onClick={generatePDF} disabled={generating || loading} style={{ background: generating ? '#94a3b8' : '#2563eb', color: 'white', border: 'none', borderRadius: '12px', padding: '12px 24px', cursor: generating ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: '600' }}>
            {generating ? 'Generando...' : 'Descargar PDF'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: '12px', padding: '4px', marginBottom: '24px', width: 'fit-content' }}>
          {['design', 'schedule'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', background: activeTab === tab ? 'white' : 'transparent', color: activeTab === tab ? '#2563eb' : '#64748b', boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
              {tab === 'design' ? 'Diseno' : 'Programar'}
            </button>
          ))}
        </div>

        {activeTab === 'design' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={card}>
                <p style={{ ...label, marginBottom: '8px' }}>Titulo del Reporte</p>
                <input value={reportTitle} onChange={e => setReportTitle(e.target.value)} style={{ ...sel, fontSize: '16px', fontWeight: '600' }} placeholder="Titulo del reporte..." />
              </div>
              <div style={card}>
                <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>Filtros de Datos</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <p style={label}>Cliente</p>
                    <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} style={sel}>
                      <option value="all">Todos los clientes</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <p style={label}>Plataforma</p>
                    <select value={selectedPlatform} onChange={e => setSelectedPlatform(e.target.value)} style={sel}>
                      <option value="all">Todas</option>
                      {Object.entries(PLATFORMS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <p style={label}>Periodo</p>
                    <select value={dateRange} onChange={e => setDateRange(e.target.value)} style={sel}>
                      {DATE_RANGES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '10px' }}>
                  {[{ val: filtered.length, label: 'campanas', color: '#2563eb' }, { val: '$' + totalSpent.toLocaleString('es-CL', { maximumFractionDigits: 0 }), label: 'gasto', color: '#10b981' }, { val: totalClicks.toLocaleString(), label: 'clicks', color: '#f59e0b' }, { val: totalConv.toLocaleString(), label: 'conversiones', color: '#8b5cf6' }].map((s, i) => (
                    <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                      <p style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: s.color }}>{s.val}</p>
                      <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div style={card}>
                <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '14px' }}>Secciones del Reporte</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {SECTIONS.map(sec => (
                    <div key={sec.id} onClick={() => setSections(prev => ({ ...prev, [sec.id]: !prev[sec.id] }))} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px', border: '2px solid ' + (sections[sec.id] ? '#2563eb' : '#e2e8f0'), background: sections[sec.id] ? '#eff6ff' : 'white', cursor: 'pointer' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '6px', border: '2px solid ' + (sections[sec.id] ? '#2563eb' : '#cbd5e1'), background: sections[sec.id] ? '#2563eb' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {sections[sec.id] && <span style={{ color: 'white', fontSize: '12px' }}>✓</span>}
                      </div>
                      <span style={{ fontSize: '16px' }}>{sec.icon}</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#0f172a' }}>{sec.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Metricas a Incluir</p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { const m = {}; ALL_METRICS.forEach(met => m[met.key] = true); setMetrics(m); }} style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>Todas</button>
                    <button onClick={() => { const m = {}; ALL_METRICS.forEach(met => m[met.key] = false); setMetrics(m); }} style={{ fontSize: '12px', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>Ninguna</button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                  {ALL_METRICS.map(met => (
                    <div key={met.key} onClick={() => setMetrics(prev => ({ ...prev, [met.key]: !prev[met.key] }))} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1.5px solid ' + (metrics[met.key] ? '#2563eb' : '#e2e8f0'), background: metrics[met.key] ? '#eff6ff' : 'white', cursor: 'pointer' }}>
                      <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: '2px solid ' + (metrics[met.key] ? '#2563eb' : '#cbd5e1'), background: metrics[met.key] ? '#2563eb' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {metrics[met.key] && <span style={{ color: 'white', fontSize: '10px' }}>✓</span>}
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{met.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <div style={{ ...card, position: 'sticky', top: '20px' }}>
                <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '14px' }}>Vista Previa</p>
                <div style={{ background: '#f8fafc', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <div style={{ background: '#2563eb', padding: '14px 16px' }}>
                    <p style={{ color: 'white', fontWeight: '700', fontSize: '13px', margin: 0 }}>{reportTitle}</p>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px', margin: '2px 0 0' }}>{DATE_RANGES.find(d => d.value === dateRange)?.label}</p>
                  </div>
                  <div style={{ padding: '12px 16px' }}>
                    {SECTIONS.filter(s => sections[s.id]).map(s => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '14px' }}>{s.icon}</span>
                        <span style={{ fontSize: '12px', color: '#374151' }}>{s.label}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '8px 16px 14px' }}>
                    <p style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', marginBottom: '6px' }}>METRICAS</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {ALL_METRICS.filter(m => metrics[m.key]).map(m => (
                        <span key={m.key} style={{ background: '#eff6ff', color: '#2563eb', fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: '500' }}>{m.label}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <button onClick={generatePDF} disabled={generating || loading} style={{ width: '100%', marginTop: '14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', padding: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                  {generating ? 'Generando...' : 'Generar PDF'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div style={{ maxWidth: '640px' }}>
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Programar Envio Automatico</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>{scheduleEnabled ? 'Activo' : 'Inactivo'}</span>
                  <div onClick={() => setScheduleEnabled(!scheduleEnabled)} style={{ width: '44px', height: '24px', borderRadius: '12px', background: scheduleEnabled ? '#2563eb' : '#cbd5e1', cursor: 'pointer', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: '3px', left: scheduleEnabled ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', opacity: scheduleEnabled ? 1 : 0.5, pointerEvents: scheduleEnabled ? 'auto' : 'none' }}>
                <div>
                  <p style={label}>Email de destino</p>
                  <input value={scheduleEmail} onChange={e => setScheduleEmail(e.target.value)} style={sel} placeholder="email@empresa.com" type="email" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <p style={label}>Frecuencia</p>
                    <select value={scheduleFreq} onChange={e => setScheduleFreq(e.target.value)} style={sel}>
                      {FREQ_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <p style={label}>{scheduleFreq === 'weekly' ? 'Dia de la semana' : 'Dia del mes'}</p>
                    {scheduleFreq === 'weekly' ? (
                      <select value={scheduleDay} onChange={e => setScheduleDay(e.target.value)} style={sel}>
                        {['Lunes','Martes','Miercoles','Jueves','Viernes'].map((d, i) => <option key={i} value={i+1}>{d}</option>)}
                      </select>
                    ) : (
                      <select value={scheduleDay} onChange={e => setScheduleDay(e.target.value)} style={sel}>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                <div>
                  <p style={label}>Periodo del reporte</p>
                  <select value={dateRange} onChange={e => setDateRange(e.target.value)} style={sel}>
                    {DATE_RANGES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                {scheduleEnabled && scheduleEmail && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px' }}>
                    <p style={{ fontSize: '13px', color: '#166534', fontWeight: '600', margin: '0 0 4px' }}>Resumen del programa</p>
                    <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>Se enviara reporte {FREQ_OPTIONS.find(f => f.value === scheduleFreq)?.label.toLowerCase()} a {scheduleEmail}</p>
                  </div>
                )}
                <button onClick={saveSchedule} style={{ background: scheduleSaved ? '#10b981' : '#2563eb', color: 'white', border: 'none', borderRadius: '10px', padding: '13px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                  {scheduleSaved ? 'Guardado' : 'Guardar Programacion'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
